import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { DistributorsConfigFormTemplateFormSectionName } from 'data/template/template.types';
import { ChangedField, FieldUpdateHandlerParam } from 'shared/form';
// import { useDialedDigitExtractionServerFieldCreateNew }
// from 'routes/DialedDigitExtractionServer/hooks/useDialedDigitExtractionServerFieldCreateNew';
import { FormLayoutContent, FormLayoutFooter, FormLayout } from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import ProcessOptionsForm from 'routes/ProcessOptions/components/ProcessOptionsForm';
import { Ref } from 'routes/ProcessOptions/components/ProcessOptionsForm/ProcessOptionsForm';
import * as processOptionsSelectors from 'data/processOption/processOption.selectors';
import { RootState } from 'data/root.reducers';
import * as processOptionsActions from 'data/processOption/processOption.actions';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import * as securityInfoActions from 'data/securityInfo/securityInfo.actions';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import { ProcessOption } from 'data/processOption/processOption.types';
import { processOptionsTemplate } from '../../templates';
import { adaptTemplateVisibilityMode } from '../../utils/dialedDigitExtractionServerTemplate.adapter';
import { getAuditDetailsToSubmit } from '../../utils/dialedDigitExtractionServerDataToSubmit';

type RouterParams = {
  history: string
}
const mapState = (state: RootState) => ({
  deliveryFunctionId: state.global.selectedDF,
  selectedProcessOption: processOptionsSelectors
    .getSelectedProcessOptions(state)[0],
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  originalSelectedProcessOption: processOptionsSelectors
    .getSelectedProcessOptions(state)[0],
  processOption: processOptionsSelectors.getProcessOptions(state),
});

const mapDispatch = {
  getSecurityInfos: securityInfoActions.getSecurityInfos,
  goToProcessOptionsList: processOptionsActions.goToProcessOptionsList,
  goToProcessOptionsEdit: processOptionsActions.goToProcessOptionsEdit,
  getProcessOptions: processOptionsActions.getProcessOptions,
};
const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter;

export const ProcessOptionsView = ({
  history,
  selectedProcessOption,
  deliveryFunctionId,
  userId,
  userName,
  isAuditEnabled,
  goToProcessOptionsList,
  goToProcessOptionsEdit,
  getProcessOptions,
}: Props): JSX.Element => {

  const formRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<Partial<ProcessOption>>({});
  const [template, setTemplate] = useState(adaptTemplateVisibilityMode(processOptionsTemplate, 'view'));
  const { t } = useTranslation();

  const id = selectedProcessOption?.id;

  useEffect(() => {
    if (id) {
      getProcessOptions(deliveryFunctionId, id);
    }
  }, [deliveryFunctionId, id, getProcessOptions]);

  useEffect(() => {
    if (selectedProcessOption != null && template != null) {
      setFormData(selectedProcessOption);

      const clonedTemplate = cloneDeep(template);
      clonedTemplate.formId = Date.now().toString();
      setTemplate(clonedTemplate);
    }
  }, [selectedProcessOption, id]);

  const handleClickEdit = () => goToProcessOptionsEdit(history);

  const handleCancel = () => goToProcessOptionsList(history);

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
    if (formData != null) {
      return;
    }

    const clonedFormData = cloneDeep(formData as ProcessOption);

    setFormData({
      ...clonedFormData,
      [fieldName]: value,
    });
  };

  const renderForm = () => {
    if (formData && template) {
      return (
        <ProcessOptionsForm
          ref={formRef}
          data={formData}
          template={template}
          isEditing
          onFieldUpdate={handleFieldUpdate}
        />
      );
    }
  };

  const viewProcessOptionTitle = 'View Process Option';
  const renderFormPanel = (formTitle:string) => {
    return (
      <FormPanel title={viewProcessOptionTitle}>
        <FormButtons
          isEditing={false}
          onClickCancel={handleCancel}
          onClickEdit={handleClickEdit}
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };

  return (
    <div data-testid="ProcessOptionsView">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel(t('ss8Distributor.view.title'))}
          {renderForm()}
        </FormLayoutContent>
      </FormLayout>
    </div>
  );
};

export default withRouter(connector(ProcessOptionsView));
